import React, { ReactNode } from 'react';
import { useEffect } from 'react';
import { matchPath, Navigate, Outlet, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import { useNavigate } from '@hooks/useNavigate';
import {
  BLOCKED_ROUTES_WHEN_CONTRACT_HAS_ENDED,
  FIXED_PATHS,
  ROUTES,
} from '@routes/routes.constants';
import { authStore } from '@stores/authStore';
import { contractStore } from '@stores/contractStore';

/** Wrapper that redirects to login if user is not authenticated */
export const PrivateRoute = observer(
  ({ children }: { children?: ReactNode }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const blockPageWhenContractHasEnded =
      contractStore.isContractEnded &&
      BLOCKED_ROUTES_WHEN_CONTRACT_HAS_ENDED.some((pattern) =>
        matchPath(pattern, location.pathname)
      );

    useEffect(() => {
      if (
        authStore.isAuthenticated ||
        authStore.canAuthenticate ||
        authStore.status === 'initial-token-refresh'
      ) {
        return;
      }

      navigate(ROUTES.LOGIN, {
        query: {
          redirect: location.pathname + location.hash + location.search,
        },
      });
    }, [
      authStore.isAuthenticated,
      authStore.canAuthenticate,
      authStore.status,
    ]);

    if (blockPageWhenContractHasEnded) {
      return <Navigate to={FIXED_PATHS.USERS_CONTRACT_HAS_ENDED} replace />;
    }

    // `children` prop is used only in tests where data routers (createBrowserRouter etc.) do not work.
    // TODO: get data routers to work in tests
    return authStore.isAuthenticated &&
      authStore.status !== 'initial-token-refresh'
      ? (children ?? <Outlet />)
      : null;
  }
);
